import { useState, useEffect, useCallback } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  getMarketplaceListings,
  getMarketplaceCategories,
  createMarketplaceListing,
  updateMarketplaceListing,
  deleteMarketplaceListing,
  uploadMarketplaceImage,
  getPublicImageUrl,
  type MarketplaceListing,
  type ListingFilters
} from "@/lib/marketplaceAPI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import {
  Plus,
  Trash2,
  Package,
  Edit,
  RefreshCw,
  Search,
  X,
  ChevronLeft,
  AlertTriangle,
  Eye,
  EyeOff,
  Loader2,
  Tag,
  Upload,
  ImagePlus
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { useDropzone } from "react-dropzone";
import AdminUsedProductForm from "./AdminUsedProductForm";

export function UsedMachineryTab() {
  const { toast } = useToast();

  // Estados
  const [searchQuery, setSearchQuery] = useState("");
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [currentListing, setCurrentListing] = useState<Partial<MarketplaceListing> | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [filters, setFilters] = useState<ListingFilters>({
    page: 1,
    limit: 10,
    sort: "newest"
  });
  const [uploadingImages, setUploadingImages] = useState(false);
  const [localImages, setLocalImages] = useState<{file: File, preview: string}[]>([]);

  // Queries
  const {
    data: listingsResponse,
    isLoading: isLoadingListings,
    refetch: refetchListings
  } = useQuery({
    queryKey: ["marketplace-listings-admin", filters],
    queryFn: () => getMarketplaceListings(filters),
  });

  // Las categorías se usan directamente en el formulario de AdminUsedProductForm
  useQuery({
    queryKey: ["marketplace-categories-admin"],
    queryFn: () => getMarketplaceCategories(),
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: Omit<MarketplaceListing, 'id' | 'created_at'>) =>
      createMarketplaceListing(data),
    onSuccess: () => {
      toast({
        title: "Producto creado",
        description: "El producto ha sido creado correctamente",
      });
      refetchListings();
      setIsEditDialogOpen(false);
      setCurrentListing(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Ha ocurrido un error al crear el producto",
        variant: "destructive",
      });
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string, data: Partial<MarketplaceListing> }) =>
      updateMarketplaceListing(id, data),
    onSuccess: () => {
      toast({
        title: "Producto actualizado",
        description: "El producto ha sido actualizado correctamente",
      });
      refetchListings();
      setIsEditDialogOpen(false);
      setCurrentListing(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Ha ocurrido un error al actualizar el producto",
        variant: "destructive",
      });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteMarketplaceListing(id),
    onSuccess: () => {
      toast({
        title: "Producto eliminado",
        description: "El producto ha sido eliminado correctamente",
      });
      refetchListings();
      setIsDeleting(false);
      setDeleteId(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Ha ocurrido un error al eliminar el producto",
        variant: "destructive",
      });
      setIsDeleting(false);
    }
  });

  // Handlers
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setFilters(prev => ({ ...prev, page: 1, query: searchQuery || undefined }));
  };

  // Tipo local para las especificaciones técnicas
  interface TechnicalSpec {
    label: string;
    value: string;
  }

  // Tipo extendido para manejar category_id como string
  type LocalMarketplaceListing = Omit<MarketplaceListing, 'id' | 'created_at' | 'category_id'> & {
    id?: string;
    category_id: string;
  };

  const handleCreateNew = () => {
    setCurrentListing({
      title: "",
      description: "",
      price: 0,
      brand: "",
      images: [],
      condition: "good",
      category_id: "",
      location: "",
      year: new Date().getFullYear(),
      is_active: true,
      features: [],
      technical_specs: []
    });
    setIsEditDialogOpen(true);
  };

  const handleEdit = (listing: MarketplaceListing) => {
    setCurrentListing(listing);
    setIsEditDialogOpen(true);
  };

  // Función para subir imágenes al servidor
  const uploadImages = async (): Promise<string[]> => {
    if (localImages.length === 0) {
      return currentListing?.images || [];
    }

    setUploadingImages(true);
    const uploadedUrls: string[] = [];
    const currentUrls = [...(currentListing?.images || [])];

    try {
      // Subir cada imagen
      for (let i = 0; i < localImages.length; i++) {
        try {
          // Subir la imagen al servidor
          const filePath = await uploadMarketplaceImage(localImages[i].file);

          // Obtener la URL pública
          const publicUrl = getPublicImageUrl(filePath);
          uploadedUrls.push(publicUrl);
        } catch (error) {
          console.error("Error al subir imagen:", error);
          toast({
            title: "Error al subir imagen",
            description: "No se pudo subir una o más imágenes",
            variant: "destructive"
          });
        }
      }

      // Limpiar las previsualizaciones locales
      localImages.forEach(image => URL.revokeObjectURL(image.preview));
      setLocalImages([]);

      // Combinar las URLs existentes con las nuevas
      return [...currentUrls, ...uploadedUrls];
    } finally {
      setUploadingImages(false);
    }
  };

  // Modificación del handleSave para subir imágenes
  const handleSave = async () => {
    if (!currentListing) return;

    if (!currentListing.title || !currentListing.description || !currentListing.brand) {
      toast({
        title: "Campos requeridos",
        description: "Por favor complete todos los campos obligatorios",
        variant: "destructive",
      });
      return;
    }

    try {
      // Subir imágenes si hay algunas locales
      const allImageUrls = await uploadImages();

      // Crear una copia con todas las imágenes
      const listingToSave = {
        ...currentListing,
        images: allImageUrls,
        category_id: currentListing.category_id as string
      };

      if ('id' in currentListing && currentListing.id) {
        updateMutation.mutate({
          id: currentListing.id,
          data: listingToSave
        });
      } else {
        createMutation.mutate(listingToSave as LocalMarketplaceListing);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Hubo un problema al guardar el producto",
        variant: "destructive",
      });
    }
  };

  const handleDelete = (id: string) => {
    setDeleteId(id);
    setIsDeleting(true);
  };

  const confirmDelete = () => {
    if (deleteId) {
      deleteMutation.mutate(deleteId);
    }
  };

  const handleChangePage = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleAddFeature = () => {
    if (!currentListing) return;

    const featureText = prompt("Introduce la característica:");
    if (featureText) {
      setCurrentListing({
        ...currentListing,
        features: [...(currentListing.features || []), featureText]
      });
    }
  };

  const handleRemoveFeature = (index: number) => {
    if (!currentListing?.features) return;

    const newFeatures = [...currentListing.features];
    newFeatures.splice(index, 1);
    setCurrentListing({
      ...currentListing,
      features: newFeatures
    });
  };

  // Renderizado de la tabla
  const renderTable = () => {
    const listings = listingsResponse?.data || [];

    if (isLoadingListings) {
      return (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-[#FCAA0D]" />
          <span className="ml-3 text-white/60">Cargando productos...</span>
        </div>
      );
    }

    if (listings.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
          <div className="bg-white/5 p-4 rounded-full mb-4">
            <Package className="h-12 w-12 text-white/40" />
          </div>
          <h3 className="text-xl font-medium text-white mb-2">No hay productos de ocasión</h3>
          <p className="text-white/60 max-w-md mb-6">
            No se encontraron productos de maquinaria usada en el sistema. Añade tu primer producto para empezar a gestionar tu inventario.
          </p>
          <Button
            variant="default"
            onClick={handleCreateNew}
            className="bg-[#FCAA0D] hover:bg-[#FCAA0D]/90 text-black font-medium"
          >
            <Plus className="mr-2 h-4 w-4" />
            Añadir producto
          </Button>
        </div>
      );
    }

    return (
      <div className="rounded-md overflow-hidden border border-white/10">
        <Table className="w-full">
          <TableHeader className="bg-[#1A1A1C]">
            <TableRow className="hover:bg-transparent border-white/10">
              <TableHead className="text-white/70 font-medium">Título</TableHead>
              <TableHead className="text-white/70 font-medium">Precio</TableHead>
              <TableHead className="text-white/70 font-medium">Marca</TableHead>
              <TableHead className="text-white/70 font-medium">Estado</TableHead>
              <TableHead className="text-white/70 font-medium">Ubicación</TableHead>
              <TableHead className="text-white/70 font-medium">Activo</TableHead>
              <TableHead className="text-white/70 font-medium text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {listings.map((listing) => (
              <TableRow key={listing.id} className="hover:bg-white/5 border-white/10">
                <TableCell className="font-medium text-white">{listing.title}</TableCell>
                <TableCell className="text-white/80">{listing.price.toLocaleString('es-ES')} €</TableCell>
                <TableCell className="text-white/80">{listing.brand}</TableCell>
                <TableCell>
                  <Badge variant={getConditionVariant(listing.condition)} className="capitalize">
                    {getConditionLabel(listing.condition)}
                  </Badge>
                </TableCell>
                <TableCell className="text-white/80">{listing.location}</TableCell>
                <TableCell>
                  {listing.is_active ? (
                    <Badge variant="outline" className="bg-green-900/20 text-green-400 border-green-500/20">
                      Activo
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-gray-800/50 text-gray-400 border-gray-700">
                      Inactivo
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(listing)}
                      className="h-8 w-8 text-white/70 hover:text-white hover:bg-white/10"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(listing.id)}
                      className="h-8 w-8 text-white/70 hover:text-red-500 hover:bg-red-500/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  // Paginación
  const renderPagination = () => {
    const totalPages = listingsResponse?.pagination?.pages || 1;
    const currentPage = filters.page || 1;

    if (totalPages <= 1) return null;

    return (
      <div className="flex justify-center gap-2 mt-6">
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleChangePage(currentPage - 1)}
          disabled={currentPage === 1}
          className="text-white/70 border-white/10 bg-[#1A1A1C] hover:bg-white/5 hover:text-white"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Anterior
        </Button>
        <div className="flex items-center px-3 bg-[#1A1A1C] rounded-md text-white/80 border border-white/10">
          Página {currentPage} de {totalPages}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleChangePage(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="text-white/70 border-white/10 bg-[#1A1A1C] hover:bg-white/5 hover:text-white"
        >
          Siguiente
          <ChevronLeft className="h-4 w-4 ml-1 rotate-180" />
        </Button>
      </div>
    );
  };

  // Funciones auxiliares
  const getConditionLabel = (condition: string): string => {
    switch (condition) {
      case 'excellent': return 'Excelente';
      case 'very_good': return 'Muy bueno';
      case 'good': return 'Bueno';
      case 'needs_repair': return 'Necesita reparación';
      default: return condition;
    }
  };

  const getConditionVariant = (condition: string): "default" | "outline" | "secondary" | "destructive" => {
    switch (condition) {
      case 'excellent': return "default";
      case 'very_good': return "secondary";
      case 'good': return "outline";
      case 'needs_repair': return "destructive";
      default: return "outline";
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white mb-1">Maquinaria de Ocasión</h2>
          <p className="text-white/60">
            Gestiona los productos de ocasión, {listingsResponse?.pagination?.total || 0} productos en total
          </p>
        </div>
        <Button
          onClick={handleCreateNew}
          className="bg-[#FCAA0D] hover:bg-[#FCAA0D]/90 text-black font-medium"
        >
          <Plus className="mr-2 h-4 w-4" />
          Nuevo producto
        </Button>
      </div>

      <div className="mb-6">
        <Card className="bg-[#111213] border-white/10 shadow-md">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-lg">Filtros</CardTitle>
            <CardDescription className="text-white/60">
              Busca y filtra los productos de maquinaria usada
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSearch} className="flex gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/50" />
                <Input
                  type="text"
                  placeholder="Buscar por título, marca, descripción..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-[#1A1A1C] border-white/10 focus-visible:ring-[#FCAA0D] text-white placeholder:text-white/40"
                />
              </div>
              <Button
                type="submit"
                className="bg-[#FCAA0D] hover:bg-[#FCAA0D]/90 text-black font-medium min-w-24"
              >
                Buscar
              </Button>
              {filters.query && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setSearchQuery("");
                    setFilters(prev => ({ ...prev, query: undefined }));
                  }}
                  className="text-white/70 hover:text-white hover:bg-white/5"
                >
                  <X className="h-4 w-4 mr-2" />
                  Limpiar
                </Button>
              )}
            </form>
          </CardContent>
        </Card>
      </div>

      {renderTable()}
      {renderPagination()}

      {/* Diálogo para añadir/editar productos */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-7xl w-[95vw] bg-[#111213] text-white border-white/10">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white">
              {currentListing?.id ? 'Editar producto' : 'Añadir nuevo producto'}
            </DialogTitle>
            <DialogDescription className="text-white/60">
              {currentListing?.id
                ? 'Modifica los detalles del producto de ocasión'
                : 'Añade un nuevo producto a tu inventario de maquinaria usada'}
            </DialogDescription>
          </DialogHeader>

          {/* Reemplazar todo el formulario manual por el componente AdminUsedProductForm */}
          <AdminUsedProductForm
            productId={currentListing?.id ? currentListing.id : null}
            onComplete={() => {
              setIsEditDialogOpen(false);
              setCurrentListing(null);
              setLocalImages([]);
              refetchListings();
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Diálogo de confirmación para eliminar */}
      <Dialog open={isDeleting} onOpenChange={setIsDeleting}>
        <DialogContent className="max-w-md bg-[#111213] text-white border-white/10">
          <DialogHeader>
            <div className="flex justify-center mb-2">
              <div className="p-3 rounded-full bg-red-500/10">
                <AlertTriangle className="h-6 w-6 text-red-500" />
              </div>
            </div>
            <DialogTitle className="text-center text-xl font-bold text-white">
              ¿Eliminar producto?
            </DialogTitle>
            <DialogDescription className="text-center text-white/60">
              Esta acción no se puede deshacer. El producto será eliminado permanentemente de la base de datos.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col sm:flex-row sm:justify-center gap-2 mt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsDeleting(false);
                setDeleteId(null);
              }}
              className="border-white/10 text-white hover:text-white hover:bg-white/5"
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={confirmDelete}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {deleteMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
